import {ContentTypes} from './ContentTypes';

declare global {
  var onYouTubeIframeAPIReady: () => void;
}

export function isHTMLElement(node: Node): node is HTMLElement {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    (node as HTMLElement).dataset !== undefined
  );
}

export function isContentVimeoPlayer(
  contentType: ContentTypes,
  player: CallbackPlayer,
): player is Vimeo.Player {
  return contentType == ContentTypes.VIMEO;
}

export function isContentYouTubePlayer(
  contentType: ContentTypes,
  player: CallbackPlayer,
): player is YT.Player {
  return contentType == ContentTypes.YOUTUBE;
}

const iconLibraryUrl = `${getShoelaceBaseUrl()}utilities/icon-library.js`;
const materialIconMutator = function (svg: SVGElement) {
  svg.setAttribute('fill', 'currentColor');
  const style = document.createElement('style');
  style.innerHTML = `path { fill: currentColor; }`;
  svg.appendChild(style);
};
let iconLibraryLoader: Promise<any> | null = null;
function ensureIconLibraryLoaded() {
  if (!iconLibraryLoader) {
    iconLibraryLoader = import(/* webpackIgnore: true */ iconLibraryUrl).then(
      (lib) => {
        lib.registerIconLibrary('default', {
          resolver: (name: string) =>
            `${getBaseUrl()}icons/material/300/outlined/${name}.svg`,
          mutator: materialIconMutator,
        });
        lib.registerIconLibrary('sport', {
          resolver: (name: string) => `${getBaseUrl()}icons/sport/${name}.svg`,
          mutator: materialIconMutator,
        });
        lib.registerIconLibrary('product', {
          resolver: (name: string) =>
            `${getBaseUrl()}icons/product/${name}.svg`,
        });
        lib.registerIconLibrary('hudl', {
          resolver: (name: string) => `${getBaseUrl()}icons/hudl/${name}.svg`,
          mutator: materialIconMutator,
        });
        lib.registerIconLibrary('country', {
          resolver: (name: string) =>
            `${getBaseUrl()}icons/country/${name}.svg`,
        });
      },
    );
  }

  return iconLibraryLoader;
}

const loadingStylesheets: Map<string, Promise<void>> = new Map();

export async function ensureStylesheetLoaded(url: string): Promise<void> {
  const resolvedUrl = URL.parse(url, window.location.href);

  if (!resolvedUrl) {
    return Promise.reject(new Error(`Invalid URL: ${url}`));
  }

  const isLoaded =
    [...document.styleSheets].filter((x) => x.href === resolvedUrl.href)
      .length > 0;
  if (isLoaded) {
    return;
  }

  if (!loadingStylesheets.has(resolvedUrl.href)) {
    loadingStylesheets.set(
      resolvedUrl.href,
      new Promise((resolve, reject) => {
        const node = Object.assign(document.createElement('link'), {
          href: url,
          rel: 'stylesheet',
          onload: () => {
            resolve(void 0);
          },
          onerror: () => {
            reject();
          },
        });
        document.head.appendChild(node);
      }),
    );
  }

  return loadingStylesheets.get(resolvedUrl.href);
}

declare global {
  var shoelaceThemeUrl: string | undefined;
  var brand5BaseUrl: string | undefined;
}

export function getBaseUrl() {
  return window.brand5BaseUrl || '/';
}

export function getShoelaceBaseUrl() {
  return 'https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.20.1/cdn/';
}

export async function ensureShoelaceThemeLoaded() {
  const promises = [ensureIconLibraryLoaded()];

  if (window.shoelaceThemeUrl) {
    promises.push(ensureStylesheetLoaded(window.shoelaceThemeUrl));
  } else {
    promises.push(
      ensureShoelaceLightThemeLoaded(),
      ensureShoelaceDarkThemeLoaded(),
    );
  }

  return Promise.all(promises);
}

export async function ensureShoelaceLightThemeLoaded() {
  return ensureStylesheetLoaded(`${getShoelaceBaseUrl()}themes/light.css`);
}

export async function ensureShoelaceDarkThemeLoaded() {
  return ensureStylesheetLoaded(`${getShoelaceBaseUrl()}themes/dark.css`);
}

let loadingHubSpotForms: Promise<any> | null = null;

export async function ensureHubSpotFormsLoaded() {
  if (window.HubSpotFormsV4) {
    return window.HubSpotFormsV4;
  }

  const url = 'https://js.hsforms.net/forms/embed/48941102.js';

  if (!loadingHubSpotForms) {
    loadingHubSpotForms = new Promise((resolve, reject) => {
      const node = Object.assign(document.createElement('script'), {
        src: url,
        onload: () => {
          resolve(window.HubSpotFormsV4);
        },
        onerror: () => {
          reject();
        },
        defer: true,
      });
      document.head.appendChild(node);
    });
  }

  return loadingHubSpotForms;
}

export async function ensureVimeoLoaded() {
  return import(
    /* webpackIgnore: true */ 'https://player.vimeo.com/api/player.js'
  );
}

let youtubePromise: Promise<any>;
export async function ensureYouTubeLoaded() {
  if (window.YT && window.YT.Player) {
    return window.YT;
  }

  const url = 'https://www.youtube.com/iframe_api';
  const isLoaded =
    [...document.scripts].filter((x) => x.src === url).length > 0;
  if (isLoaded) {
    return youtubePromise;
  }

  youtubePromise = new Promise((resolve, reject) => {
    const origReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (origReady) {
        origReady();
      }

      resolve(window.YT);
    };

    const node = Object.assign(document.createElement('script'), {
      src: url,
      onerror: () => {
        reject();
      },
    });
    document.head.appendChild(node);
  });
  return youtubePromise;
}

export function isElementVimeo(element: HTMLElement) {
  return element.dataset.vimeoId || element.dataset.vimeoUrl;
}

export function getLazyVimeoDataset(element: HTMLElement) {
  if (
    !(
      isElementVimeo(element) ||
      element.dataset.lazyVimeoId ||
      element.dataset.lazyVimeoUrl
    )
  ) {
    return null;
  }

  return {
    vimeoId: element.dataset.lazyVimeoId || element.dataset.vimeoId,
    vimeoUrl: element.dataset.lazyVimeoUrl || element.dataset.vimeoUrl,
  };
}

function isElementYouTube(element: HTMLElement): element is HTMLIFrameElement {
  return (
    element.tagName === 'IFRAME' &&
    /^https:\/\/www.youtube.com\/embed\//.test(
      (element as HTMLIFrameElement).src,
    )
  );
}

function isElementYouTubeReplaceable(element: HTMLElement) {
  return element.dataset.youtubeId || element.dataset.youtubeUrl;
}

function enableYouTubeApiUrl(url: URL) {
  const enableParam = ['enablejsapi', '1'];
  if (url.searchParams.get(enableParam[0]) != enableParam[1]) {
    url.searchParams.set(enableParam[0], enableParam[1]);
    return true;
  }

  return false;
}

function limitYouTubeRecommendations(url: URL) {
  const relParam = ['rel', '0'];
  if (url.searchParams.get(relParam[0]) != relParam[1]) {
    url.searchParams.set(relParam[0], relParam[1]);
    return true;
  }

  return false;
}

export function ensureYouTubeApiEnabled(element: HTMLElement) {
  if (!isElementYouTube(element)) {
    return false;
  }

  const url = URL.parse(element.src);
  if (url && enableYouTubeApiUrl(url)) {
    element.src = url.href;
  }

  return true;
}

type ContentCallback<T extends ContentTypes> = (
  contentType: T,
  player: T extends ContentTypes.VIMEO
    ? Vimeo.Player
    : T extends ContentTypes.YOUTUBE
      ? YT.Player
      : null,
) => void;
type CallbackPlayer = YT.Player | Vimeo.Player | null;

export function handleContentPlaceholders(
  target: HTMLElement,
  callback: (contentType: ContentTypes, player: CallbackPlayer) => void,
) {
  const vimeoData = getLazyVimeoDataset(target);
  if (vimeoData) {
    const vimeoId = vimeoData.vimeoId || '';
    let vimeoUrl = vimeoData.vimeoUrl;
    const idAndHash = vimeoId.split('/');
    if (idAndHash.length > 1) {
      delete target.dataset.vimeoId;
      vimeoUrl = `https://player.vimeo.com/video/${idAndHash[0]}?h=${idAndHash[1]}`;
      target.dataset.vimeoUrl = vimeoUrl;
    } else if (vimeoUrl) {
      target.dataset.vimeoUrl = vimeoUrl;
    } else if (vimeoId) {
      target.dataset.vimeoId = vimeoId;
    }

    target.innerHTML = '';
    ensureVimeoLoaded().then(() => {
      const player = new Vimeo.Player(target);
      (callback as ContentCallback<ContentTypes.VIMEO>)(
        ContentTypes.VIMEO,
        player,
      );
    });
    return;
  }

  if (isElementYouTubeReplaceable(target)) {
    const youtubeId = target.dataset.youtubeId;
    let youtubeUrl = target.dataset.youtubeUrl;
    if (!youtubeUrl) {
      youtubeUrl = `https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&rel=0&origin=${encodeURIComponent(window.origin)}`;
    }
    const url = URL.parse(youtubeUrl);
    if (url) {
      enableYouTubeApiUrl(url);
      limitYouTubeRecommendations(url);
    }

    target.innerHTML = '';
    const placeholder = Object.assign(document.createElement('iframe'), {
      src: url?.href,
      width: 640,
      height: 390,
    });
    target.appendChild(placeholder);

    ensureYouTubeLoaded().then(() => {
      const player = new YT.Player(placeholder, {
        events: {
          onReady: () => {
            (callback as ContentCallback<ContentTypes.YOUTUBE>)(
              ContentTypes.YOUTUBE,
              player,
            );
          },
        },
      });
    });
    return;
  }

  const hubSpotFormId = target.dataset.formId;
  if (hubSpotFormId) {
    target.innerHTML = '';
    const placeholder = Object.assign(document.createElement('div'), {
      className: 'hs-form-frame',
    });
    Object.assign(placeholder.dataset, {
      region: 'na1',
      formId: hubSpotFormId,
      portalId: '48941102',
    });
    target.appendChild(placeholder);

    ensureHubSpotFormsLoaded().then(() => {
      (callback as ContentCallback<ContentTypes.HUBSPOT_FORM>)(
        ContentTypes.HUBSPOT_FORM,
        null,
      );
    });

    return;
  }
}
